import React, { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { footerSubText, footerText, footerLinks } from "./contents";
import { openInNewTab } from "@/lib/utils";

type Props = object;

function Footer({}: Props) {
  // Memoize the gradient style to prevent recreation on every render
  const footerTitleStyle = useMemo(
    () => ({
      background: "linear-gradient(180deg, #F5F5F5 0%, #D0D0D0 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    }),
    [],
  );

  return (
    <footer
      id="footer"
      className="relative mx-auto w-full max-w-6xl px-4 pt-24 pb-10 text-center text-white"
    >
      <h1
        className="font-dm mx-auto max-w-[1200px] text-[clamp(40px,6.5vw,88px)] leading-[92px] font-semibold tracking-[-0.03em]"
        style={footerTitleStyle}
      >
        {footerText}
      </h1>

      <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-zinc-300">
        {footerSubText}
      </p>

      <div className="mx-auto mt-6 flex w-full max-w-[560px] flex-wrap items-center justify-center gap-3">
        <SocialIcon
          src="/home/social/email.svg"
          alt="email"
          href="mailto:hello@pasoma.ai"
        />
        <SocialIcon
          src="/home/social/discord.svg"
          alt="discord"
          href="https://discord.gg/ndvCksmvWs"
        />
        <SocialIcon
          src="/home/social/twitter.svg"
          alt="x"
          href="https://x.com/pasoma_ai"
        />
        <SocialIcon
          src="/home/social/linkedin.svg"
          alt="whatsapp"
          href="https://linkedin.com/company/pasoma"
        />

        <GradientOutlineButton href="https://cal.com/haruna-faruk-zf16cx/pasoma-cli-installation">
          <span className="mr-1">Schedule a Meeting</span>
          <span aria-hidden>→</span>
        </GradientOutlineButton>
      </div>

      <div className="mx-auto mt-16 flex w-full max-w-6xl flex-col items-center justify-between gap-4 text-sm text-zinc-400 md:flex-row">
        <p>© 2025 Pasoma. All rights reserved.</p>
        <div className="flex items-center gap-6">
          {footerLinks.map((l) => (
            <Link
              key={l.title}
              href={l.link}
              className="transition-all duration-200 hover:text-white"
            >
              {l.title}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}

export default Footer;

// Helpers
function SocialIcon({
  src,
  alt,
  href,
}: {
  src: string;
  alt: string;
  href: string;
}) {
  return (
    <button
      type="button"
      onClick={() => openInNewTab(href)}
      aria-label={alt}
      className="group relative cursor-pointer overflow-hidden rounded-[15px] p-[1px] transition-all duration-300 [background:linear-gradient(180deg,#FFFFFF_0%,#787878_100%)]"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-[15px] shadow-[inset_-4px_4px_4px_0px_rgba(61,57,57,0.25)] transition-all duration-300 [background:radial-gradient(50%_50%_at_50%_50%,#151414_0%,#181818_100%)] group-hover:shadow-[inset_-4px_4px_4px_0px_rgba(61,57,57,0.4)]">
        <Image
          src={src}
          alt={alt}
          width={28}
          height={28}
          className="transition-transform duration-300"
        />
      </span>
      {/* Subtle hover glow */}
      <div className="absolute inset-0 rounded-[15px] bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
    </button>
  );
}

function GradientOutlineButton({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      target="_blank"
      className="group relative overflow-hidden rounded-[42px] p-[1px] transition-all duration-300 [background:linear-gradient(180deg,#FFFFFF_0%,#787878_100%)]"
    >
      <span className="font-dm inline-flex h-[54px] min-w-[184px] items-center justify-center rounded-[42px] px-[17px] text-[18px] leading-[24px] font-semibold tracking-[-0.05em] shadow-[inset_-4px_4px_4px_0px_rgba(61,57,57,0.25)] transition-all duration-300 [background:radial-gradient(50%_50%_at_50%_50%,#151414_0%,#181818_100%)] group-hover:shadow-[inset_-4px_4px_4px_0px_rgba(61,57,57,0.4)]">
        {children}
      </span>
      {/* Subtle hover glow */}
      <div className="absolute inset-0 rounded-[42px] bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
    </Link>
  );
}
